import { Plate } from '@/components/landing/primitives';
import { stagger } from '@/components/landing/stagger';
import { LoopVideo } from '@/components/landing/loop-video';
import { PILLARS } from '@/components/landing/inference-layers';
import { CLIPS, CLIP_SIZE } from '@/content/physical-ai';

/**
 * The three pillars as three full-width statements — the title large on the
 * left, the explanation on the right — instead of three small cards. Each row
 * carries the id the hero's kicker links to.
 *
 * Physical AI is the pillar a visitor most needs to *see*, so it closes with
 * three short real loops of the robots at work (owner's footage). They play
 * only while on screen; their captions are text, so they read without video.
 */
export function WhatSection() {
  return (
    <Plate eyebrow="What we do" id="what" labelledBy="what-title">
      <h2 id="what-title" className="lp-h2 lp-reveal max-w-4xl" style={stagger(1)}>
        Three ways to put AI to work
      </h2>
      <p className="lp-body lp-reveal mt-6 max-w-2xl" style={stagger(2)}>
        Choose where your AI runs. We set it up, connect it to your work, and keep it running.
      </p>

      <ol className="mt-16 md:mt-20">
        {PILLARS.map((p, i) => (
          <li
            key={p.id}
            id={p.id}
            aria-labelledby={`${p.id}-title`}
            className="what-row lp-reveal"
            style={stagger(3 + i)}
          >
            <div>
              <p className="eyebrow">{p.name}</p>
              <h3 id={`${p.id}-title`} className="t-h3-xl mt-4">
                {p.title}
              </h3>
            </div>
            <div>
              <p className="text-[17px] leading-relaxed text-[hsl(var(--fg-secondary))] md:text-lg">{p.line}</p>
              <p className="mt-4 text-[15px] text-muted-foreground">
                <span className="text-foreground/80">Good for: </span>
                {p.goodFor}
              </p>
            </div>
          </li>
        ))}
      </ol>

      <div className="lp-reveal border-t border-border pt-10" style={stagger(6)}>
        <p className="eyebrow">Robots in our lab</p>
        <div className="mt-6 grid gap-5 md:grid-cols-3">
          {CLIPS.map((c) => (
            <figure key={c.id}>
              <LoopVideo
                src={c.src}
                poster={c.poster}
                width={CLIP_SIZE.width}
                height={CLIP_SIZE.height}
                label={`${c.title}: ${c.line}`}
                className="aspect-video w-full rounded-xl bg-card object-cover"
              />
              <figcaption className="mt-4">
                <span className="block text-[15px] font-semibold text-foreground">{c.title}</span>
                <span className="mt-1 block text-[15px] leading-snug text-muted-foreground">{c.line}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </Plate>
  );
}
